import { notification } from 'antd';
import { FormikBag, FormikErrors } from 'formik';

import { apiClient } from '../../../graphql/api/apiClient';
import {
  EmergencyScenarioStatus,
  EmergencyScenarioTableFragment,
} from '../../../graphql/api/generated';
import { createEmergencyScenario, updateEmergencyScenario } from '../../../graphql/api/operations';
import { displayErrorMessage } from '../../../utils';

export interface IEmergencyScenarioFormProps {
  isNew: boolean;
  emergencyScenario?: EmergencyScenarioTableFragment;
  onCancel: () => any;
  onSubmit: (emergencyScenarioId: string) => any;
}

export interface IEmergencyScenarioFormValues {
  propertyId?: string;
  name?: string;
  status?: EmergencyScenarioStatus;
}

export function mapPropsToValues(props: IEmergencyScenarioFormProps): IEmergencyScenarioFormValues {
  const { emergencyScenario } = props;

  return {
    propertyId: emergencyScenario?.property.propertyId,
    name: emergencyScenario?.name,
    status: emergencyScenario?.status,
  };
}

export function validate(values: IEmergencyScenarioFormValues) {
  const errors: FormikErrors<IEmergencyScenarioFormValues> = {};

  if (!values.propertyId) {
    errors.propertyId = 'Please select a property';
  }

  if (!values.name) {
    errors.name = 'Please provide a name';
  }

  return errors;
}

export async function handleSubmit(
  values: IEmergencyScenarioFormValues,
  formikBag: FormikBag<IEmergencyScenarioFormProps, IEmergencyScenarioFormValues>
) {
  try {
    formikBag.setSubmitting(true);

    const { isNew, emergencyScenario, onSubmit } = formikBag.props;
    const { propertyId, name, status } = values;

    if (isNew) {
      if (!propertyId || !name) {
        throw new Error('Missing emergency scenario information');
      }

      const { emergencyScenarioId } = await createEmergencyScenario({ propertyId, name }) || {};

      if (!emergencyScenarioId) {
        throw new Error('Unable to create emergency scenario');
      }

      onSubmit?.(emergencyScenarioId);
    } else if (emergencyScenario) {
      await updateEmergencyScenario(emergencyScenario.emergencyScenarioId, {
        name: name || undefined,
        status: status || undefined,
      });

      onSubmit?.(emergencyScenario.emergencyScenarioId);
    }

    notification.success({
      message: 'Success',
      description: `The emergency scenario was successfully ${isNew ? 'created' : 'updated'}!`,
    });

    await apiClient.resetStore();
  } catch (error) {
    displayErrorMessage(error);
  }

  formikBag.setSubmitting(false);
}
